import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Activity, Battery, MapPin, Wallet, History, Siren, Users,
  Settings, BarChart3, Cpu, LogOut, Radio, ShieldAlert, ChevronDown,
  AlertTriangle, Layers, Sparkles,
} from "lucide-react";
import { cn } from "../lib/utils";
import { StatusPill } from "./Pills";

const NAV = {
  consumer: [
    { to: "/app/consumer/stations", icon: MapPin, label: "Stations" },
    { to: "/app/consumer/sessions", icon: Activity, label: "My Sessions" },
    { to: "/app/consumer/wallet", icon: Wallet, label: "Wallet" },
    { to: "/app/consumer/history", icon: History, label: "History" },
  ],
  operator: [
    { to: "/app/operator/fleet", icon: Radio, label: "Fleet" },
    { to: "/app/operator/revenue", icon: BarChart3, label: "Revenue" },
    { to: "/app/operator/alerts", icon: AlertTriangle, label: "Alerts" },
  ],
  public_safety: [
    { to: "/app/safety/zones", icon: Layers, label: "Zone Map" },
    { to: "/app/safety/emergency", icon: Siren, label: "Emergency Control" },
    { to: "/app/safety/critical", icon: ShieldAlert, label: "Critical Devices" },
  ],
  admin: [
    { to: "/app/admin/network", icon: BarChart3, label: "Network" },
    { to: "/app/admin/stations", icon: Radio, label: "Stations" },
    { to: "/app/admin/users", icon: Users, label: "Users" },
    { to: "/app/admin/policies", icon: Settings, label: "Policies" },
    { to: "/app/admin/ai", icon: Sparkles, label: "AI Insights" },
  ],
};

const ROLE_LABEL = {
  consumer: "Consumer",
  operator: "Operator",
  public_safety: "Public Safety",
  admin: "Admin",
};

export default function Layout() {
  const { user, activeRole, switchRole, logout } = useAuth();
  const navigate = useNavigate();
  const [emergency, setEmergency] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const tick = async () => {
      try {
        const { data } = await api.get("/emergency");
        setEmergency(data);
      } catch (_) { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 6000);
    return () => clearInterval(t);
  }, []);

  if (!user || !activeRole) return null;
  const items = NAV[activeRole] || [];

  return (
    <div className="flex min-h-screen bg-[#050505] text-white">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className="w-64 border-r border-zinc-800 flex flex-col bg-[#0a0a0c] shrink-0"
      >
        <div className="px-5 py-6 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 border border-cyan-400 grid place-items-center">
              <Battery className="w-3.5 h-3.5 text-cyan-400" />
            </div>
            <div>
              <div className="font-black text-lg tracking-tighter leading-none">BIN</div>
              <div className="bin-label">Energy OS</div>
            </div>
          </div>
        </div>

        {/* Role switcher */}
        <div className="px-3 py-3 border-b border-zinc-800 relative">
          <button
            data-testid="role-switcher"
            onClick={() => setOpen((o) => !o)}
            className="w-full flex items-center justify-between px-3 py-2 border border-zinc-800 rounded text-sm hover:border-cyan-500/50"
          >
            <span className="flex items-center gap-2">
              <Cpu className="w-3.5 h-3.5 text-cyan-400" />
              <span className="font-mono uppercase text-xs tracking-wider">{ROLE_LABEL[activeRole]}</span>
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
          </button>
          {open && (
            <div className="absolute left-3 right-3 top-[3.6rem] z-30 bin-card-elevated p-1">
              {user.roles.map((r) => (
                <button
                  key={r}
                  data-testid={`role-option-${r}`}
                  onClick={() => {
                    switchRole(r);
                    setOpen(false);
                    navigate(`/app/${r === "public_safety" ? "safety" : r}/${
                      r === "consumer" ? "stations" :
                      r === "operator" ? "fleet" :
                      r === "public_safety" ? "zones" : "network"
                    }`);
                  }}
                  className={cn(
                    "w-full text-left px-3 py-2 text-xs font-mono uppercase tracking-wider rounded",
                    r === activeRole ? "bg-cyan-500/10 text-cyan-400" : "hover:bg-white/5"
                  )}
                >
                  {ROLE_LABEL[r]}
                </button>
              ))}
            </div>
          )}
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors",
                  isActive
                    ? "bg-cyan-500/10 text-cyan-400 border-l-2 border-cyan-400"
                    : "text-zinc-400 hover:bg-white/5 hover:text-white"
                )
              }
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-zinc-800">
          <div className="px-3 py-2 mb-2">
            <div className="bin-label">Operator</div>
            <div className="text-sm font-mono truncate">{user.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user.email}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-wider text-zinc-400 hover:bg-white/5 rounded"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="border-b border-zinc-800 px-6 py-3 flex items-center justify-between bg-[#08080a]">
          <div className="flex items-center gap-3">
            <span className="bin-label">Battery Internet Network</span>
            <span className="text-zinc-700">/</span>
            <span className="text-sm font-mono">{ROLE_LABEL[activeRole]} Console</span>
          </div>
          <div className="flex items-center gap-3">
            {emergency?.active && (
              <div
                data-testid="emergency-banner"
                className="flex items-center gap-2 px-3 py-1 border border-red-500/50 bg-red-500/10 rounded"
              >
                <Siren className="w-3.5 h-3.5 text-red-400" />
                <span className="text-xs font-mono uppercase tracking-wider text-red-400">
                  Emergency Mode {emergency.zone ? `· ${emergency.zone}` : "· Network-wide"}
                </span>
              </div>
            )}
            <StatusPill status="online" />
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
